import { useState, useCallback } from 'react';
import { Outlet, NavLink, Link } from 'react-router-dom';
import styles from './DocsLayout.module.css';

interface NavItem {
	label: string;
	path: string;
}

interface NavSection {
	title: string;
	items: NavItem[];
}

interface ExternalLink {
	label: string;
	href: string;
	icon: 'play' | 'admin' | 'operator';
}

const externalLinks: ExternalLink[] = [
	{ label: 'Demo Games', href: 'https://play.rngfort.com', icon: 'play' },
	{ label: 'Admin Dashboard', href: 'https://admin.rngfort.com', icon: 'admin' },
	{
		label: 'Operator Dashboard',
		href: 'https://operator.rngfort.com',
		icon: 'operator',
	},
];

const navigationSections: NavSection[] = [
	{
		title: 'Getting Started',
		items: [
			{ label: 'Overview', path: '/docs' },
			{ label: 'Quickstart', path: '/docs/quickstart' },
			{ label: 'Authentication', path: '/docs/authentication' },
			{ label: 'Environments', path: '/docs/environments' },
		],
	},
	{
		title: 'Integration',
		items: [
			{ label: 'Integration Guide', path: '/docs/integration-guide' },
			{ label: 'Player Sessions', path: '/docs/sessions' },
			{ label: 'Wallet Modes', path: '/docs/wallet-modes' },
			{ label: 'Webhooks', path: '/docs/webhooks' },
		],
	},
	{
		title: 'API Reference',
		items: [
			{ label: 'Operator API', path: '/docs/api-operators' },
			{ label: 'Game API', path: '/docs/api-games' },
			{ label: 'Admin API', path: '/docs/api-admin' },
			{ label: 'Error Codes', path: '/docs/errors' },
		],
	},
	{
		title: 'Games',
		items: [
			{ label: 'Dice', path: '/docs/games-dice' },
			{ label: 'Plinko', path: '/docs/games-plinko' },
			{ label: 'Coin Flip', path: '/docs/games-coinflip' },
			{ label: 'Crash', path: '/docs/games-crash' },
		],
	},
	{
		title: 'Testing',
		items: [
			{ label: 'Testing Guide', path: '/docs/testing-guide' },
			{ label: 'Provably Fair', path: '/docs/provably-fair' },
		],
	},
];

export default function DocsLayout() {
	const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

	const toggleMobileMenu = useCallback(() => {
		setIsMobileMenuOpen((prev) => !prev);
	}, []);

	const closeMobileMenu = useCallback(() => {
		setIsMobileMenuOpen(false);
	}, []);

	return (
		<div className={styles.layout}>
			{/* Mobile Header */}
			<header className={styles.mobileHeader}>
				<Link to="/docs" className={styles.mobileLogo}>
					<svg
						className={styles.logoIcon}
						width="24"
						height="24"
						viewBox="0 0 24 24"
						fill="none"
						xmlns="http://www.w3.org/2000/svg"
					>
						<path
							d="M12 2L2 7L12 12L22 7L12 2Z"
							stroke="currentColor"
							strokeWidth="2"
							strokeLinecap="round"
							strokeLinejoin="round"
						/>
						<path
							d="M2 17L12 22L22 17"
							stroke="currentColor"
							strokeWidth="2"
							strokeLinecap="round"
							strokeLinejoin="round"
						/>
						<path
							d="M2 12L12 17L22 12"
							stroke="currentColor"
							strokeWidth="2"
							strokeLinecap="round"
							strokeLinejoin="round"
						/>
					</svg>
					<span>Ringfort Docs</span>
				</Link>
				<button
					className={styles.mobileMenuButton}
					onClick={toggleMobileMenu}
					aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
					aria-expanded={isMobileMenuOpen}
				>
					{isMobileMenuOpen ? (
						<svg
							width="24"
							height="24"
							viewBox="0 0 24 24"
							fill="none"
							xmlns="http://www.w3.org/2000/svg"
						>
							<path
								d="M18 6L6 18M6 6L18 18"
								stroke="currentColor"
								strokeWidth="2"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
						</svg>
					) : (
						<svg
							width="24"
							height="24"
							viewBox="0 0 24 24"
							fill="none"
							xmlns="http://www.w3.org/2000/svg"
						>
							<path
								d="M3 12H21M3 6H21M3 18H21"
								stroke="currentColor"
								strokeWidth="2"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
						</svg>
					)}
				</button>
			</header>

			{/* Mobile Menu Overlay */}
			{isMobileMenuOpen && (
				<div
					className={styles.mobileOverlay}
					onClick={closeMobileMenu}
					aria-hidden="true"
				/>
			)}

			{/* Sidebar */}
			<aside
				className={`${styles.sidebar} ${isMobileMenuOpen ? styles.sidebarOpen : ''}`}
			>
				<div className={styles.sidebarHeader}>
					<Link to="/docs" className={styles.logo} onClick={closeMobileMenu}>
						<svg
							className={styles.logoIcon}
							width="28"
							height="28"
							viewBox="0 0 24 24"
							fill="none"
							xmlns="http://www.w3.org/2000/svg"
						>
							<path
								d="M12 2L2 7L12 12L22 7L12 2Z"
								stroke="currentColor"
								strokeWidth="2"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
							<path
								d="M2 17L12 22L22 17"
								stroke="currentColor"
								strokeWidth="2"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
							<path
								d="M2 12L12 17L22 12"
								stroke="currentColor"
								strokeWidth="2"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
						</svg>
						<span className={styles.logoText}>Ringfort Docs</span>
					</Link>
				</div>

				<nav className={styles.nav}>
					{navigationSections.map((section) => (
						<div key={section.title} className={styles.navSection}>
							<h3 className={styles.navSectionTitle}>{section.title}</h3>
							<ul className={styles.navList}>
								{section.items.map((item) => (
									<li key={item.path}>
										<NavLink
											to={item.path}
											end={item.path === '/docs'}
											className={({ isActive }) =>
												`${styles.navLink} ${isActive ? styles.navLinkActive : ''}`
											}
											onClick={closeMobileMenu}
										>
											{item.label}
										</NavLink>
									</li>
								))}
							</ul>
						</div>
					))}

					{/* External Links Section */}
					<div className={styles.navSection}>
						<h3 className={styles.navSectionTitle}>Platform</h3>
						<ul className={styles.navList}>
							{externalLinks.map((link) => (
								<li key={link.href}>
									<a
										href={link.href}
										target="_blank"
										rel="noopener noreferrer"
										className={styles.externalLink}
										onClick={closeMobileMenu}
									>
										{link.icon === 'play' && (
											<svg
												width="14"
												height="14"
												viewBox="0 0 24 24"
												fill="none"
												stroke="currentColor"
												strokeWidth="2"
											>
												<polygon points="5 3 19 12 5 21 5 3" />
											</svg>
										)}
										{link.icon === 'admin' && (
											<svg
												width="14"
												height="14"
												viewBox="0 0 24 24"
												fill="none"
												stroke="currentColor"
												strokeWidth="2"
											>
												<path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
											</svg>
										)}
										{link.icon === 'operator' && (
											<svg
												width="14"
												height="14"
												viewBox="0 0 24 24"
												fill="none"
												stroke="currentColor"
												strokeWidth="2"
											>
												<rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
												<line x1="3" y1="9" x2="21" y2="9" />
												<line x1="9" y1="21" x2="9" y2="9" />
											</svg>
										)}
										<span>{link.label}</span>
										<svg
											width="12"
											height="12"
											viewBox="0 0 24 24"
											fill="none"
											stroke="currentColor"
											strokeWidth="2"
											className={styles.externalIcon}
										>
											<path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
											<polyline points="15 3 21 3 21 9" />
											<line x1="10" y1="14" x2="21" y2="3" />
										</svg>
									</a>
								</li>
							))}
						</ul>
					</div>
				</nav>

				<div className={styles.sidebarFooter}>
					<Link to="/" className={styles.backToHome} onClick={closeMobileMenu}>
						<svg
							width="16"
							height="16"
							viewBox="0 0 24 24"
							fill="none"
							xmlns="http://www.w3.org/2000/svg"
						>
							<path
								d="M19 12H5M5 12L12 19M5 12L12 5"
								stroke="currentColor"
								strokeWidth="2"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
						</svg>
						<span>Back to Home</span>
					</Link>
				</div>
			</aside>

			{/* Main Content */}
			<main className={styles.main}>
				<div className={styles.content}>
					<Outlet />
				</div>
			</main>
		</div>
	);
}
